<script lang="ts">
	import { portal } from 'svelte-portal'
	import { scale } from 'svelte/transition'
	import { cubicOut } from 'svelte/easing'
	import { tick } from 'svelte'

	/** Distance from tooltip edge to arrow center for corner positions (keeps arrow away from corner) */
	const CORNER_ARROW_INSET = 32

	export interface TooltipProps {
		text?: string
		html?: string
		tooltipSize?: 'small' | 'medium' | 'large'
		delay?: number
		position?:
			| 'top'
			| 'bottom'
			| 'left'
			| 'right'
			| 'top-left'
			| 'top-right'
			| 'bottom-left'
			| 'bottom-right'
		disabled?: boolean
		class?: string // Additional classes for the wrapper
		offset?: number // Distance from the trigger element
		children?: any
		maxWidth?: string // Max width of the tooltip
		minWidth?: string // Min width of the tooltip
		animationDuration?: number // Animation duration in milliseconds
		content?: import('svelte').Snippet
		visible?: boolean
		controlled?: boolean
		backgroundColor?: string
		onVisibilityChange?: (visible: boolean) => void
	}

	let {
		text = '',
		html,
		tooltipSize = 'medium',
		position = 'top',
		disabled = false,
		class: wrapperClass = '',
		offset = 8,
		delay = 150,
		children,
		minWidth,
		maxWidth,
		content,
		animationDuration = 200,
		visible = $bindable(false),
		controlled = false,
		backgroundColor = '#042126',
		onVisibilityChange
	}: TooltipProps = $props()

	let isShown = $state(false)
	const isVisible = $derived(controlled ? visible : isShown)
	let triggerElement: HTMLElement | null = $state(null)
	let tooltipElement: HTMLElement | null = $state(null)
	let tooltipPosition = $state({ top: 0, left: 0 })

	let hideTimeoutId: ReturnType<typeof setTimeout> | null = null

	function clearHideTimeout() {
		if (hideTimeoutId !== null) {
			clearTimeout(hideTimeoutId)
			hideTimeoutId = null
		}
	}

	function setShown(visible: boolean) {
		isShown = visible
		onVisibilityChange?.(visible)
	}

	const tooltipSizeClass = $derived(
		{
			small: 'tooltip-small',
			medium: 'tooltip-medium',
			large: 'tooltip-large'
		}[tooltipSize] || ''
	)

	const positionClass = $derived(
		{
			top: 'position-top',
			bottom: 'position-bottom',
			left: 'position-left',
			right: 'position-right',
			'top-left': 'position-top-left',
			'top-right': 'position-top-right',
			'bottom-left': 'position-bottom-left',
			'bottom-right': 'position-bottom-right'
		}[position] || ''
	)

	const arrowClass = $derived(
		{
			top: 'arrow-top',
			bottom: 'arrow-bottom',
			left: 'arrow-left',
			right: 'arrow-right',
			'top-left': 'arrow-top-left',
			'top-right': 'arrow-top-right',
			'bottom-left': 'arrow-bottom-left',
			'bottom-right': 'arrow-bottom-right'
		}[position] || ''
	)

	const transformOrigin = $derived(
		{
			top: 'center bottom',
			bottom: 'center top',
			left: 'right center',
			right: 'left center',
			'top-left': 'right bottom',
			'top-right': 'left bottom',
			'bottom-left': 'right top',
			'bottom-right': 'left top'
		}[position] || 'center'
	)

	async function calculateTooltipPosition() {
		if (!triggerElement) return

		const rect = triggerElement.getBoundingClientRect()

		const triggerCenterX = rect.left + rect.width / 2
		const triggerCenterY = rect.top + rect.height / 2

		let top = 0
		let left = 0

		switch (position) {
			case 'top':
				top = rect.top - offset
				left = triggerCenterX
				break
			case 'bottom':
				top = rect.bottom + offset
				left = triggerCenterX
				break
			case 'left':
				top = triggerCenterY
				left = rect.left - offset
				break
			case 'right':
				top = triggerCenterY
				left = rect.right + offset
				break
			case 'top-left':
				top = rect.top - offset
				left = triggerCenterX + CORNER_ARROW_INSET
				break
			case 'top-right':
				top = rect.top - offset
				left = triggerCenterX - CORNER_ARROW_INSET
				break
			case 'bottom-left':
				top = rect.bottom + offset
				left = triggerCenterX + CORNER_ARROW_INSET
				break
			case 'bottom-right':
				top = rect.bottom + offset
				left = triggerCenterX - CORNER_ARROW_INSET
				break
		}

		tooltipPosition = { top, left }

		await tick()
		if (!tooltipElement) return

		const viewportPadding = 8
		const tooltipRect = tooltipElement.getBoundingClientRect()
		const horizontalCorrection =
			(tooltipRect.left < viewportPadding ? viewportPadding - tooltipRect.left : 0) +
			(tooltipRect.right > window.innerWidth - viewportPadding
				? window.innerWidth - viewportPadding - tooltipRect.right
				: 0)
		const verticalCorrection =
			(tooltipRect.top < viewportPadding ? viewportPadding - tooltipRect.top : 0) +
			(tooltipRect.bottom > window.innerHeight - viewportPadding
				? window.innerHeight - viewportPadding - tooltipRect.bottom
				: 0)

		if (horizontalCorrection !== 0 || verticalCorrection !== 0) {
			tooltipPosition = {
				top: top + verticalCorrection,
				left: left + horizontalCorrection
			}
		}
	}

	async function handleMouseEnter() {
		if (disabled) return

		clearHideTimeout()
		await tick()
		calculateTooltipPosition()

		setShown(true)
	}

	function handleMouseLeave() {
		if (disabled) return

		hideTimeoutId = setTimeout(() => {
			hideTimeoutId = null
			setShown(false)
		}, delay)
	}

	function handleTooltipMouseEnter() {
		clearHideTimeout()
		setShown(true)
	}

	function handleTooltipMouseLeave() {
		hideTimeoutId = setTimeout(() => {
			hideTimeoutId = null
			setShown(false)
		}, delay)
	}
</script>

<div
	class="tooltip-wrapper {wrapperClass}"
	bind:this={triggerElement}
	onmouseenter={handleMouseEnter}
	onmouseleave={handleMouseLeave}
	role="tooltip"
	aria-describedby={isVisible ? 'tooltip' : undefined}
>
	{@render children?.()}

	{#if isVisible}
		<div
			use:portal
			bind:this={tooltipElement}
			class="tooltip {tooltipSizeClass} {positionClass}"
			style="position: fixed; top: {tooltipPosition.top}px; left: {tooltipPosition.left}px; z-index: 9999; transform-origin: {transformOrigin}; max-width: {maxWidth ||
				'inherit'}; min-width: {minWidth || 'inherit'}; --tooltip-bg: {backgroundColor};"
			onmouseenter={handleTooltipMouseEnter}
			onmouseleave={handleTooltipMouseLeave}
			id="tooltip"
			role="tooltip"
			in:scale={{
				duration: animationDuration,
				start: 0.8,
				opacity: 0,
				easing: cubicOut
			}}
			out:scale={{
				duration: animationDuration,
				start: 0.8,
				opacity: 0,
				easing: cubicOut
			}}
		>
			{#if content}
				{@render content()}
			{:else if html}
				{@html html}
			{:else}
				{text}
			{/if}
			<div class="tooltip-arrow {arrowClass}"></div>
		</div>
	{/if}
</div>

<style>
	.tooltip-wrapper {
		@apply relative inline-flex;
	}

	.tooltip {
		@apply text-white rounded shadow-lg max-w-xs;
		background-color: var(--tooltip-bg, #042126);
		will-change: transform, opacity;
	}

	.tooltip-arrow {
		@apply absolute w-0 h-0 border-4;
	}

	.tooltip-small {
		@apply text-xs px-2 py-1;
	}

	.tooltip-medium {
		@apply text-sm px-3 py-2;
	}

	.tooltip-large {
		@apply text-sm px-8 py-6 rounded-xl;

		.tooltip-arrow {
			@apply border-[10px];
		}

		/* Keep corner arrows centered on target: large arrow is 20px wide, center at CORNER_ARROW_INSET (32px) from edge */
		.arrow-top-left,
		.arrow-bottom-left {
			@apply right-[22px]; /* 32 - 10 = 22px from right */
		}

		.arrow-top-right,
		.arrow-bottom-right {
			@apply left-[22px]; /* 32 - 10 = 22px from left */
		}
	}

	.position-top {
		@apply transform -translate-x-1/2 -translate-y-full;
	}

	.position-bottom {
		@apply transform -translate-x-1/2;
	}

	.position-left {
		@apply transform -translate-x-full -translate-y-1/2;
	}

	.position-right {
		@apply transform -translate-y-1/2;
	}

	.position-top-left {
		@apply transform -translate-x-full -translate-y-full;
	}

	.position-top-right {
		@apply transform -translate-y-full;
	}

	.position-bottom-left {
		@apply transform -translate-x-full;
	}

	.position-bottom-right {
		/* No transform needed - tooltip grows from the position point */
	}

	.arrow-top {
		@apply top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent;
		border-top-color: var(--tooltip-bg, #042126);
	}

	.arrow-bottom {
		@apply bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent;
		border-bottom-color: var(--tooltip-bg, #042126);
	}

	.arrow-left {
		@apply left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent;
		border-left-color: var(--tooltip-bg, #042126);
	}

	.arrow-right {
		@apply right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent;
		border-right-color: var(--tooltip-bg, #042126);
	}

	.arrow-top-left {
		@apply top-full right-7 border-l-transparent border-r-transparent border-b-transparent;
		border-top-color: var(--tooltip-bg, #042126);
	}

	.arrow-top-right {
		@apply top-full left-7 border-l-transparent border-r-transparent border-b-transparent;
		border-top-color: var(--tooltip-bg, #042126);
	}

	.arrow-bottom-left {
		@apply bottom-full right-7 border-l-transparent border-r-transparent border-t-transparent;
		border-bottom-color: var(--tooltip-bg, #042126);
	}

	.arrow-bottom-right {
		@apply bottom-full left-7 border-l-transparent border-r-transparent border-t-transparent;
		border-bottom-color: var(--tooltip-bg, #042126);
	}
</style>
